import {
  Card,
  ProgressBar,
  Button,
  Banner,
  Text,
  Link,
  BlockStack,
  InlineStack,
  Bleed,
  Box,
  InlineGrid,
  Icon,
  Thumbnail,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { TranslateLink } from "@shared/ui";
import { SubscriptionData, SubscriptionMode, BillingPeriod, PlanPricing } from "../types";
import { useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/en";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { User } from "@shared/types/user";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { EditIcon } from "@medentic/polaris-icons";

const getPeriodTranslationKey = (period: BillingPeriod): string => {
  const periodKeyMap: Record<BillingPeriod, string> = {
    [BillingPeriod.MONTHLY]: "settings.subscription.period.month",
    [BillingPeriod.ANNUAL]: "settings.subscription.period.year",
  };
  return periodKeyMap[period] || `settings.subscription.period.${period}`;
};

interface CurrentPlanCardProps {
  data: SubscriptionData;
  clinicId: string;
}

export function CurrentPlanCard({ data, clinicId }: CurrentPlanCardProps) {
  const { t } = useTranslation();
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const queryClient = useQueryClient();
  const { DELETE, POST, GET } = useApiRequest();

  // Determine which pricing to show based on billing period
  const billingPeriod =
    data.currentSubscription.billingPeriod || BillingPeriod.MONTHLY;
  const currentPricing = data.currentSubscription.active.pricing.find(
    (p: PlanPricing) => p.period === billingPeriod
  );

  const user = queryClient.getQueryData<User>(["user"]);
  const userTimezone = user?.timezone?.id ?? "UTC";


  // Get payment method icon
  const getPaymentMethodIcon = (type: string) => {
    const iconMap: { [key: string]: string } = {
      visa: "/images/svg/payment-methods/visa.svg",
      mastercard: "/images/svg/payment-methods/mastercard.svg",
      amex: "/images/svg/payment-methods/amex.svg",
      discover: "/images/svg/payment-methods/discover.svg",
    };
    return iconMap[type.toLowerCase()];
  };


  // Cancel subscription mutation
  const cancelSubscriptionMutation = useMutation({
    mutationFn: () => {
      return DELETE(`/api/healthcare-companies/${clinicId}/billing/subscription`);
    },
    onSuccess: () => {
      // Invalidate subscription-related queries to refresh the component
      queryClient.invalidateQueries({ 
        queryKey: ["subscription", clinicId] 
      });
    },
  });

  // Switch to annual billing mutation
  type SubscriptionRedirectResponse = { data?: { link?: string } };
  const switchToAnnualMutation = useMutation({
    mutationFn: () => {
      return POST<SubscriptionRedirectResponse>(
        `/api/healthcare-companies/${clinicId}/billing/subscription`,
        {
          productId: data.currentSubscription.active.id,
          billingInterval: BillingPeriod.ANNUAL,
          redirectURI: window.location.href,
          productType: "subscription",
        }
      );
    },
    onSuccess: (response) => {
      // The API returns a redirect link in data.link
      if (response?.data?.link) {
        window.location.href = response.data.link;
      }
      // Also invalidate queries in case the redirect doesn't happen immediately
      queryClient.invalidateQueries({ 
        queryKey: ["subscription", clinicId] 
      });
    },
  });

  // Edit payment method mutation
  type PaymentMethodRedirectResponse = { link?: string };
  const editPaymentMethodMutation = useMutation({
    mutationFn: () => {
      return GET<PaymentMethodRedirectResponse>(
        `/api/healthcare-companies/${clinicId}/billing/subscription/change-payment-method`
      );
    },
    onSuccess: (response) => {
      // The API returns a redirect link in data.link
      if (response?.link) {
        window.location.href = response.link;
      }
    },
  });

  // Early return if pricing not found - this indicates data inconsistency
  if (!currentPricing) {
    return null;
  }


  const banner = data.currentSubscription.banner;

  const formattedNextBillingDate: string = dayjs
    .utc(data.currentSubscription.nextBillingDate)
    .tz(userTimezone)
    .format("LL");

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack gap="400" align="space-between">
          
          <Text variant="headingMd" as="h2">
            {t("settings.subscription.currentPlan")}
          </Text>
          {data.currentSubscription.mode === SubscriptionMode.PAID && (
          <Button
              variant="plain"
              tone="critical"
              onClick={() => cancelSubscriptionMutation.mutate()}
              loading={cancelSubscriptionMutation.isPending}
              accessibilityLabel={t("settings.subscription.cancelSubscription")}
            >
              {t("settings.subscription.cancelSubscription")}
            </Button>
          )}
        </InlineStack>

        {data.currentSubscription.mode === SubscriptionMode.FREE && data.currentSubscription.usage && (
          <BlockStack gap="200">
            <ProgressBar
              progress={data.currentSubscription.usage.percent}
              size="small"
              tone={data.currentSubscription.usage.percent >= 100 ? "critical" : "primary"}
            />
            <Text as="p" variant="bodyMd">
              {t("settings.subscription.usageText", {
                current: data.currentSubscription.usage.current,
                max: data.currentSubscription.usage.max,
              })}
            </Text>
          </BlockStack>
        )}
        <Card>
          <BlockStack gap="200">
            <Text variant="headingLg" as="p">
              {data.currentSubscription.active.label}
            </Text>
            <InlineStack gap="100" blockAlign="baseline">
                <Text variant="headingLg" as="p">
                  {currentPricing.priceLabel ?? "0"}
                </Text>
                <Text variant="bodySm" as="span" tone="subdued">
                  {t(getPeriodTranslationKey(currentPricing.period))}
                </Text>
            </InlineStack>
            {data.currentSubscription.nextBillingDate && (
              <Text as="p" variant="bodyMd">
                {t("settings.subscription.nextBilling")}: {formattedNextBillingDate}
              </Text>
            )}
            <TranslateLink text={data.currentSubscription.active.description} />
          </BlockStack>
        </Card>

        {/* Payment Method Card - Only show for paid subscriptions */}
        {data.currentSubscription.paymentMethod && (
          <Card>
                <InlineGrid
                  columns="auto 1fr auto"
                  gap="300"
                  alignItems="center"
                >
                  {/* Payment Method Icon */}
                  {data.currentSubscription.paymentMethod.type && (
                      <Box>
                        <Thumbnail
                                source={getPaymentMethodIcon(data.currentSubscription.paymentMethod.name)}
                                alt={`${data.currentSubscription.paymentMethod.type} icon`}
                                size="extraSmall"
                              />
                      </Box>
                    )}

                  {/* Payment Method Details */}
                  <InlineGrid
                    columns="1fr auto"
                    gap="400"
                    alignItems="center"
                  >
                    <BlockStack gap="400">
                      <InlineStack gap="200" blockAlign="center">
                        <InlineStack gap="200" wrap>
                          <Text variant="bodyMd" as="span" fontWeight="semibold">
                            {data.currentSubscription.paymentMethod.name}
                          </Text>
                          {data.currentSubscription.paymentMethod.type === "card" && (
                            <Text variant="bodyMd" as="span" tone="subdued">
                              •••• {data.currentSubscription.paymentMethod.details}
                            </Text>
                          )}
                        </InlineStack>
                      </InlineStack>
                    </BlockStack>
                  </InlineGrid>

                  {/* Edit Button */}
                  <Box>
                    <BlockStack align="center">
                      <Button
                        variant="tertiary"
                        icon={<Icon source={EditIcon} />}
                        size="medium"
                        onClick={() => editPaymentMethodMutation.mutate()}
                        loading={editPaymentMethodMutation.isPending}
                        accessibilityLabel={t("settings.subscription.editPaymentMethod", { defaultValue: "Edit payment method" })}
                      />
                    </BlockStack>
                  </Box>
                </InlineGrid>
          </Card>
        )}

        {data.currentSubscription.mode === SubscriptionMode.PAID && banner && isBannerVisible && (
          <Banner
            onDismiss={banner.hideCloseIcon ? undefined : () => setIsBannerVisible(false)}
            tone={banner.tone}
          >
            <p>
              {banner.title}{" "}
              {banner.action && (
                banner.id === "banner_monthly_to_yearly" ? (
                  <Button
                    variant="plain"
                    onClick={() => switchToAnnualMutation.mutate()}
                    loading={switchToAnnualMutation.isPending}
                    disabled={switchToAnnualMutation.isPending}
                  >
                    {banner.action.label}
                  </Button>
                ) : (
                  <Link url={banner.action.url}>
                    {banner.action.label}
                  </Link>
                )
              )}
            </p>
          </Banner>
        )}
        <InlineStack gap="400"></InlineStack>
        <Bleed marginInline="400" marginBlock="400">
          <Box
            background="bg-surface-secondary"
            paddingBlock="300"
            paddingInline="400"
          >
            <Text as="p" variant="bodyMd" tone="subdued">
              <TranslateLink text= {t("settings.subscription.footer_tos")} /> 
            </Text>
          </Box>
        </Bleed>
      </BlockStack>
    </Card>
  );
}
